import React, { useEffect, useRef, useState } from "react";
import { http, useAuth } from "@/context/AuthContext";
import { Loader2, Send, MessageSquare } from "lucide-react";

export default function PortalMessages() {
  const { user } = useAuth();
  const [items, setItems] = useState([]);
  const [text, setText] = useState("");
  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState(false);
  const endRef = useRef(null);

  async function load() {
    const r = await http.get("/portal/messages");
    setItems(r.data?.messages || []);
    setLoading(false);
    setTimeout(() => endRef.current?.scrollIntoView({ behavior: "smooth" }), 50);
  }

  useEffect(() => {
    load();
  }, []);

  async function handleSend(e) {
    e.preventDefault();
    if (!text.trim()) return;
    setSending(true);
    try {
      await http.post("/portal/messages", { body: text.trim() });
      setText("");
      await load();
    } finally {
      setSending(false);
    }
  }

  return (
    <div data-testid="portal-messages" className="flex flex-col">
      <h1 className="text-4xl">Messages</h1>
      <p className="mt-2 text-navy/65">Talk directly with the ConnectHer team. We respond within 1 business day.</p>

      <div className="mt-6 card p-0 flex flex-col h-[500px]">
        <div className="flex-1 overflow-y-auto p-6 space-y-4">
          {loading ? (
            <div className="flex items-center gap-2 text-navy/60"><Loader2 className="w-4 h-4 animate-spin" /> Loading…</div>
          ) : items.length === 0 ? (
            <div className="h-full flex flex-col items-center justify-center text-center text-navy/55">
              <MessageSquare className="w-10 h-10 text-gold mb-3" />
              <p>No messages yet. Send us a note and we'll get back to you.</p>
            </div>
          ) : (
            items.map((m, i) => {
              const mine = m.from === "client";
              return (
                <div key={m.message_id || i} className={`flex ${mine ? "justify-end" : "justify-start"}`} data-testid={`message-${i}`}>
                  <div className={`max-w-md rounded-2xl px-4 py-3 ${mine ? "bg-navy text-soft" : "bg-gold/10 text-navy border border-gold/20"}`}>
                    <div className={`text-[10px] uppercase tracking-widest mb-1 ${mine ? "text-soft/60" : "text-navy/50"}`}>
                      {mine ? "You" : m.from_name || "ConnectHer Team"}
                    </div>
                    <div className="text-sm leading-relaxed whitespace-pre-wrap">{m.body}</div>
                  </div>
                </div>
              );
            })
          )}
          <div ref={endRef} />
        </div>
        <form onSubmit={handleSend} className="border-t border-navy/5 p-4 flex gap-3" data-testid="message-form">
          <input
            type="text"
            value={text}
            onChange={(e) => setText(e.target.value)}
            placeholder="Type a message…"
            data-testid="message-input"
            className="flex-1 input-field"
          />
          <button type="submit" disabled={sending || !text.trim()} data-testid="message-send" className="btn-primary">
            {sending ? <Loader2 className="w-4 h-4 animate-spin" /> : <><Send className="w-4 h-4" /> Send</>}
          </button>
        </form>
      </div>
    </div>
  );
}
